import {useCallback, useState} from 'react';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import * as Yup from 'yup';

import {useContactMutation} from '../api';

const validationSchema = Yup.object({
  firstname: Yup.string().max(255, 'Firstname is too long'),
  surname: Yup.string().max(255, 'Surname is too long'),
  email: Yup.string().email('Must be an email address').max(255, 'Email is too long').required('Email is required'),
  phone: Yup.string().matches(/^[+()\d ]+$/, {
    message: 'Please enter valid number',
    excludeEmptyString: false,
  }),
  message: Yup.string().required('Please type a message'),
  // Honeypot field - must remain empty
  website: Yup.string().test('is-empty', 'Invalid submission', value => !value),
});

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  message: '',
  website: '', // Honeypot field
};

const useContactForm = () => {
  const [isSuccess, setIsSuccess] = useState();
  const [recaptchaError, setRecaptchaError] = useState(false);
  const doContactSubmit = useContactMutation();
  const {executeRecaptcha} = useGoogleReCaptcha();

  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        // Wait for reCAPTCHA to be ready
        if (!executeRecaptcha) {
          actions.setSubmitting(false);
          return;
        }

        const token = await executeRecaptcha('contact_form');

        // Submit with token
        await doContactSubmit({...values, recaptchaToken: token});
        setIsSuccess(true);

        actions.resetForm();
      } catch (e) {
        console.error(e);

        // Check if it's a reCAPTCHA error
        if (e.message && (e.message.includes('Invalid site key') || e.message.includes('not loaded'))) {
          setRecaptchaError(true);
          actions.setSubmitting(false);
          return;
        }

        if (e.status === 422) {
          actions.setErrors(e.data?.errors ?? {});
        }
        actions.setSubmitting(false);
      }
    },
    [setIsSuccess, executeRecaptcha, doContactSubmit]
  );

  return {
    initialValues,
    onSubmit,
    validationSchema,
    isSuccess,
    recaptchaError,
  };
};

export default useContactForm;
